import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Assignment,
  LocalHospital,
  People,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LabCatalogContent from '../../components/common/LabCatalogContent';
import DashboardLayout from '../../components/common/DashboardLayout';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const CheckoutPractitioner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Get patient from location state
  const selectedPatient = location.state?.selectedPatient;

  // State for cart and checkout
  const [cartItems, setCartItems] = useState([]);
  const [notesForClient, setNotesForClient] = useState('');
  const [requireFasting, setRequireFasting] = useState(false);

  // Redirect if no patient selected
  useEffect(() => {
    if (!selectedPatient) {
      navigate('/practitioner');
    }
  }, [selectedPatient, navigate]);

  // Handle logout
  const handleLogout = async () => {
    const result = await SwalWithA11yFix({
      title: 'Are you sure?',
      text: 'You will be logged out of your account',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      logout();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Logged Out!',
        text: 'You have been successfully logged out.',
        timer: 1500,
        showConfirmButton: false,
      });
      navigate('/login');
    }
  };

  // Menu items for sidebar
  const menuItems = [
    {
      icon: <Assignment />,
      label: 'Orders',
      active: false,
      onClick: () => navigate('/practitioner'),
    },
    {
      icon: <LocalHospital />,
      label: 'Lab Catalog',
      active: false,
      onClick: () => navigate('/practitioner'),
    },
    {
      icon: <People />,
      label: 'Clients',
      active: false,
      onClick: () => navigate('/practitioner'),
    },
  ];

  // Handle test selection from catalog
  const handleSelectTest = (test) => {
    const isAlreadyAdded = cartItems.some((item) => item._id === test._id);

    if (isAlreadyAdded) {
      SwalWithA11yFix({
        icon: 'info',
        title: 'Already Added',
        text: 'This test is already in your cart',
        confirmButtonColor: '#1976d2',
      });
      return;
    }

    setCartItems([
      ...cartItems,
      {
        ...test,
        quantity: 1,
      },
    ]);

    SwalWithA11yFix({
      icon: 'success',
      title: 'Added to Cart',
      text: `${test.name} has been added to your cart`,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  // Remove item from cart
  const handleRemoveItem = (testId) => {
    setCartItems(cartItems.filter((item) => item._id !== testId));
  };

  const additionalFeeAmount = 0;

  // Check if any test requires phlebotomy
  const anyTestRequiresPhlebotomy = cartItems.some(
    (item) => item.phlebotomy === 'Required'
  );

  // Handle checkout
  const handleSendToClient = async () => {
    if (cartItems.length === 0) {
      SwalWithA11yFix({
        icon: 'warning',
        title: 'Empty Cart',
        text: 'Please add at least one test to your cart',
        confirmButtonColor: '#1976d2',
      });
      return;
    }

    try {
      // Create multiple orders for each test in cart
      const orders = [];
      for (const item of cartItems) {
        const response = await api.post('/practitioner/assign-test', {
          patientId: selectedPatient._id,
          testId: item._id,
          testDate: new Date(),
          notes: notesForClient || null,
          additionalFee: additionalFeeAmount,
          requireFasting,
        });
        orders.push(response.data);
      }

      SwalWithA11yFix({
        icon: 'success',
        title: 'Order Created Successfully!',
        text: `${cartItems.length} test(s) have been added to ${selectedPatient.firstName} ${selectedPatient.lastName}'s orders`,
        confirmButtonColor: '#1976d2',
      });

      // Navigate back to orders
      navigate('/practitioner');
    } catch (error) {
      console.error('Error creating order:', error);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to create order',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  if (!selectedPatient) {
    return null;
  }

  return (
    <DashboardLayout
      title="Checkout"
      user={user}
      onLogout={handleLogout}
      menuItems={menuItems}
      logoutPath="/login"
    >
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Main Catalog Area */}
      <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
        {/* Back Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/practitioner')}
            sx={{
              textTransform: 'none',
              color: '#1976d2',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Back to Orders
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Cart for {selectedPatient.firstName} {selectedPatient.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Patient state:
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#4caf50',
                  }}
                />
                {selectedPatient.state || 'Unknown'}
              </span>
            </Typography>
          </Box>
        </Box>

        {/* Lab Catalog Content - Reused Component */}
        <LabCatalogContent
          embedded={true}
          showComparison={false}
          showSelectButton={true}
          onSelectTest={handleSelectTest}
          selectedTestIds={cartItems.map((item) => item._id)}
        />
      </Box>

      {/* Cart Sidebar */}
      <Box
        sx={{
          width: 350,
          backgroundColor: 'white',
          borderLeft: '1px solid #e0e0e0',
          p: 3,
          overflowY: 'auto',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Cart Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: '#1976d2', mb: 0.5 }}
          >
            ⊕ PHYSICIAN SERVICES
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', fontWeight: 500 }}>
            Draft Cart
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />


        {/* Selected Tests */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#666', mb: 1 }}
          >
            Selected Tests ({cartItems.length})
          </Typography>
          <Box
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              p: 1,
            }}
          >
            {cartItems.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: '#999', textAlign: 'center', py: 3 }}
              >
                No tests selected yet
              </Typography>
            ) : (
              cartItems.map((item) => (
                <Box
                  key={item._id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    p: 1.5,
                    mb: 1,
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
                    >
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {item.lab?.name || 'Unknown Lab'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveItem(item._id)}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Fasting Requirement - Only show if phlebotomy is required */}
        {anyTestRequiresPhlebotomy && (
          <Box sx={{ mb: 3, p: 1.5, backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireFasting}
                  onChange={(e) => setRequireFasting(e.target.checked)}
                  sx={{
                    color: '#1976d2',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d97706' }}>
                  ⚕️ Require Fasting
                </Typography>
              }
            />
            <Typography variant="caption" sx={{ display: 'block', ml: 4, color: '#b45309', mt: -0.5 }}>
              Some tests in this order require phlebotomy
            </Typography>
          </Box>
        )}

        {/* Notes for Client */}
        {cartItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#666' }}
              >
                Notes to Client
              </Typography>
              <IconButton
                size="small"
                sx={{ color: '#1976d2' }}
              >
                ℹ️
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={notesForClient}
              onChange={(e) => setNotesForClient(e.target.value)}
              placeholder="Add any notes for the client..."
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                },
              }}
            />
          </Box>
        )}

        {/* Actions (below Notes to Client) */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/practitioner')}
            sx={{
              borderColor: '#e0e0e0',
              color: '#666',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#d0d0d0',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSendToClient}
            disabled={cartItems.length === 0}
            sx={{
              backgroundColor: '#1976d2',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: '#bdbdbd',
              },
            }}
          >
            Submit
          </Button>
        </Box>

        <Box sx={{ mt: 'auto' }} />
      </Box>
    </Box>
    </DashboardLayout>
  );
};

export default CheckoutPractitioner;

